import { ChangeEvent, FC, useLayoutEffect, useMemo, useReducer, useState } from 'react';
import {
	ActionsContainer,
	EmptyRelatedDealerships,
	Field,
	Form,
	Label,
	RelateDealership,
} from '../../Registration.styles';
import { Button, Dropdown, ErrorScreen, Input, Loading, Table } from '../../../../components';
import { RegistrationFormPage } from '../../templates';
import { Option } from '../../../../components/dropdown/Dropdown';
import { newUserFormReducer, newUserFormInitialState } from './newUser.form';
import {
	isFieldValid,
	opcoesDePerfil,
	opcoesDeSimOuNao,
	opcoesTagNaoComprar,
} from '../../../../utils';
import { useToastStore, useUserStore } from '../../../../stores';
import { Tooltip } from '@mui/material';
import {
	getAdminUserById,
	getAllDealers,
	getAllShopkeepers,
	postAdmintUser,
	putAdmintUser,
} from '../../../../services';
import { DeleteOutlined, Report } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

interface StateObject {
	[key: string]: any;
}

const NewUser: FC = () => {
	const navigate = useNavigate();
	const { id } = useParams();
	const { setToast } = useToastStore();
	const { user } = useUserStore();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [concessionarias, setConcessionarias] = useState<Option[]>([]);
	const [revendas, setRevendas] = useState<Option[]>([]);
	const [state, dispatch] = useReducer(newUserFormReducer, newUserFormInitialState);
	const [idConcessionaria, setIdConcessionaria] = useState<string | number>(0);

	const concessionariaSelecionada = useMemo(
		() => concessionarias.find((concessionaria) => concessionaria.value === idConcessionaria),
		[concessionarias, idConcessionaria]
	);
	const revendaSelecionada = useMemo(
		() => revendas.find((revenda) => revenda.value === state.revenda),
		[revendas, state.revenda]
	);
	const perfilSelecionado = useMemo(
		() => opcoesDePerfil.find((perfil) => +perfil.value === state.idGrupoAcesso),
		[state.idGrupoAcesso]
	);
	const statusSelecionado = useMemo(
		() => opcoesDeSimOuNao.find((status) => status.value === state.tagAtivo),
		[state.tagAtivo]
	);
	
	const tagNaoComprarSelecionado = useMemo(
		() => opcoesTagNaoComprar.find((tagNaoComprar) => tagNaoComprar.value === state.tagNaoComprar),
		[state.tagNaoComprar]
	);
	const tagReceberEmailSelecionado = useMemo(
		() =>
			opcoesDeSimOuNao.find((tagReceberEmail) => tagReceberEmail.value === state.tagReceberEmail),
		[state.tagReceberEmail]
	);
	const tagReceberPushSelecionado = useMemo(
		() => opcoesDeSimOuNao.find((tagReceberPush) => tagReceberPush.value === state.tagReceberPush),
		[state.tagReceberPush]
	);
	const concessionariasRelacionadas = useMemo(() => {
		return state.concessionarias?.flatMap((stateConcessionariaId) =>
			concessionarias.filter((concessionaria) => concessionaria.value === stateConcessionariaId)
		);
	}, [state.concessionarias, concessionarias]);

	const tableColumns = [
		{ id: 'name', label: 'Nome' },
		{ id: 'actions', label: 'Ações' },
	];

	const rowMapper = (item: Option) => ({
		name: { value: item.label, rawValue: item.label },
		actions: {
			value: (
				<ActionsContainer>
					<Tooltip title='Desvincular' placement='top' arrow>
						<div>
							<Button
								variant='icon'
								icon={
									<DeleteOutlined
										fontSize='inherit'
										onClick={() => handleUnrelateConcessionaria(item.value as number)}
									/>
								}
							/>
						</div>
					</Tooltip>
				</ActionsContainer>
			),
		},
	});

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		dispatch({ type: 'SET_VALUE', field: name, payload: value });
	};

	const handleSelectOption = (field: string, option: Option) => {
		dispatch({ type: 'SET_SELECT_OPTION', field: field, value: option.value });
	};

	const handleRelateConcessionaria = () => {
		if (!concessionariaSelecionada) return;

		const concessionariaJaRelacionada = concessionariasRelacionadas.some(
			(concessionaria) => concessionaria.value === concessionariaSelecionada?.value
		);

		if (concessionariaJaRelacionada) {
			setToast({ message: 'Concessionária ja relacionada', variant: 'error' });
			return;
		}
		dispatch({
			type: 'SET_MULTISELECT_OPTION',
			field: 'concessionarias',
			value: [...state.concessionarias, concessionariaSelecionada.value as number],
		});
	};

	const handleUnrelateConcessionaria = (concessionariaId: number) => {
		const novoConcessionarias = state.concessionarias.filter(
			(concessionaria) => concessionaria !== concessionariaId
		);
		dispatch({
			type: 'SET_MULTISELECT_OPTION',
			field: 'concessionarias',
			value: novoConcessionarias,
		});
	};

	const handleCreate = async () => {
		try {
			const requestBody = {
				idGrupoAcesso: state.idGrupoAcesso,
				concessionarias: state.concessionarias || [],
				idRevenda: state.revenda,
				nome: state.nome,
				email: state.email,
				telefone: state.telefone,
				senha: state.senha,
				tagAtivo: state.tagAtivo,
				tagNaoComprar: state.tagNaoComprar || '0',
				tagReceberEmail: state.tagReceberEmail,
				tagReceberPush: state.tagReceberPush,
			};

			const response = await postAdmintUser(requestBody);

			if (!response.toString().includes('sucesso')) {
				setToast({ message: response.toString(), variant: 'error' });
			} else {
				setToast({
					message: 'Usuário criado com sucesso!',
					variant: 'success',
				});
				navigate(`/cadastros/usuarios`);
			}
		} catch {
			setToast({ message: 'Erro ao criar usuário. Tente novamente.', variant: 'error' });
		}
	};

	const handleEdit = async () => {
		try {
			const requestBody = {
				idGrupoAcesso: state.idGrupoAcesso,
				concessionarias: state.concessionarias || [],
				idRevenda: state.revenda,
				nome: state.nome,
				email: state.email,
				telefone: state.telefone,
				senha: state.senha,
				tagAtivo: state.tagAtivo,
				tagNaoComprar: state.tagNaoComprar || '0',
				tagReceberEmail: state.tagReceberEmail,
				tagReceberPush: state.tagReceberPush,
			};

			const response = await putAdmintUser(id!, requestBody);

			if (!response.toString().includes('sucesso')) {
				setToast({ message: response.toString(), variant: 'error' });
			} else {
				setToast({
					message: 'Usuário atualizado com sucesso!',
					variant: 'success',
				});
				navigate(`/cadastros/usuarios`);
			}
		} catch {
			setToast({ message: 'Erro ao atualizar usuário. Tente novamente.', variant: 'error' });
		}
	};

	const isFormValid = (stateObj: StateObject): boolean => {
		const stateCopy = { ...stateObj };

		if (perfilSelecionado?.value === 3) {
			delete stateCopy['concessionarias'];
		} else if (perfilSelecionado?.value === 2) {
			delete stateCopy['revenda'];
			delete stateCopy['tagNaoComprar'];
		} else {
			delete stateCopy['concessionarias'];
			delete stateCopy['revenda'];
			delete stateCopy['tagNaoComprar'];
		}
		if (!!id) {
			delete stateCopy['senha'];
			delete stateCopy['confirmacaoSenha'];
		}
		const values = Object.values(stateCopy);

		return values.every((value) => isFieldValid(value));
	};

	const handleSubmit = () => {
		const isValid = isFormValid(state);

		if (!isValid) {
			setToast({ message: 'Preencha todos os campos', variant: 'error' });
			return;
		}

		if (state.senha !== state.confirmacaoSenha) {
			setToast({ message: 'As senhas precisam ser iguais', variant: 'error' });
			return;
		}

		if (!!id) {
			handleEdit();
		} else {
			handleCreate();
		}
	};

	useLayoutEffect(() => {
		window.scrollTo(0, 0);

		const fetchDealerships = async () => {
			if (!user) return;

			setLoading(true);

			try {
				const concessionariasOpcoes: Option[] = [];

				const todasAsConcessionarias = await getAllDealers();

				todasAsConcessionarias?.forEach((concessionaria) => {
					if (concessionaria.tagAtivo === "1") {
						concessionariasOpcoes.push({
							value: concessionaria.id,
							label: concessionaria.fantasia || '',
						});
					}
				});
				concessionariasOpcoes.sort((a, b) => a.label.localeCompare(b.label));
				setConcessionarias(concessionariasOpcoes);
			} catch {
				setError(true);
				setToast({ message: 'Erro ao buscar concessionárias!', variant: 'error' });
			} finally {
				setTimeout(() => setLoading(false), 500);
			}
		};

		const fetchShopkeeper = async () => {
			if (!user) return;

			setLoading(true);

			try {
				const revendasOpcoes: Option[] = [];

				const todasAsRevendas = await getAllShopkeepers();

				todasAsRevendas?.forEach((revenda) => {
					revendasOpcoes.push({
						value: revenda.id,
						label: revenda.fantasia || '',
					});
				});
				revendasOpcoes.sort((a, b) => a.label.localeCompare(b.label));
				setRevendas(revendasOpcoes);
			} catch {
				setError(true);
				setToast({ message: 'Erro ao buscar revendas!', variant: 'error' });
			} finally {
				setTimeout(() => setLoading(false), 500);
			}
		};

		const fetchUser = async () => {
			if (!id) return;

			try {
				setLoading(true);

				const response = await getAdminUserById(id);


				if (!response) {
					throw new Error();
				}

				const idsConcessionarias = response.concessionarias?.map(
					(concessionaria) => concessionaria.id
				);

				const formInitialState = {
					idGrupoAcesso: response.grupoAcesso.id,
					concessionarias: idsConcessionarias || [],
					revenda: response.revenda?.id || 0,
					nome: response.nome,
					email: response.email,
					telefone: response.telefone,
					senha: response.senha,
					confirmacaoSenha: response.senha,
					tagAtivo: response.revenda?.revendaTagAtivo ?? response.tagAtivo,
					tagNaoComprar: response.tagNaoComprar,
					tagReceberEmail: response.tagReceberEmail,
					tagReceberPush: response.tagReceberPush,
				};

				dispatch({ type: 'REPLACE_STATE', payload: formInitialState });
			} catch {
				setError(true);
				setToast({ message: 'Erro ao buscar dados do usuário!', variant: 'error' });
			} finally {
				setTimeout(() => setLoading(false), 500);
			}
		};

		fetchDealerships();
		fetchShopkeeper();
		fetchUser();
	}, [user, id, setToast]);

	if (loading) return <Loading fullScreen />;

	if (error) return <ErrorScreen fullScreen />;

	return (
		<RegistrationFormPage title='Cadastrar Usuário' onSubmit={handleSubmit}>
			<Form>
				<Label>Perfil</Label>
				<Field>
					<Dropdown
						options={opcoesDePerfil}
						onSelect={(option: Option) => handleSelectOption('idGrupoAcesso', option)}
						selected={perfilSelecionado}
					/>
				</Field>
				<Label>Nome</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Nome'
						value={state.nome}
						onChange={handleInputChange}
						name='nome'
					/>
				</Field>
				<Label>Login/E-mail</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Login/E-mail'
						value={state.email}
						onChange={handleInputChange}
						name='email'
					/>
				</Field>
				<Label>Telefone</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Telefone'
						value={state.telefone}
						onChange={handleInputChange}
						name='telefone'
						maxLength={15}
					/>
				</Field>
				<Label>Senha</Label>
				<Field>
					<Input
						variant='password'
						placeholder='Senha'
						value={state.senha}
						onChange={handleInputChange}
						name='senha'
					/>
				</Field>
				<Label>Confirmação da Senha</Label>
				<Field>
					<Input
						variant='password'
						placeholder='Confirmação da Senha'
						value={state.confirmacaoSenha}
						onChange={handleInputChange}
						name='confirmacaoSenha'
					/>
				</Field>
				<Label>Ativo?</Label>
				<Field>
					<Dropdown
						options={opcoesDeSimOuNao}
						selected={statusSelecionado}
						onSelect={(option: Option) => handleSelectOption('tagAtivo', option)}
					/>
				</Field>
				{perfilSelecionado?.value === 3 && (
					<>
						<Label>Pode comprar?</Label>
						<Field>
							<Dropdown
								options={opcoesTagNaoComprar}
								selected={tagNaoComprarSelecionado}
								onSelect={(option: Option) => handleSelectOption('tagNaoComprar', option)}
							/>
						</Field>
					</>
				)}
				<Label>Receber Emails?</Label>
				<Field>
					<Dropdown
						options={opcoesDeSimOuNao}
						selected={tagReceberEmailSelecionado}
						onSelect={(option: Option) => handleSelectOption('tagReceberEmail', option)}
					/>
				</Field>
				<Label>Receber Push?</Label>
				<Field>
					<Dropdown
						options={opcoesDeSimOuNao}
						selected={tagReceberPushSelecionado}
						onSelect={(option: Option) => handleSelectOption('tagReceberPush', option)}
					/>
				</Field>
				{perfilSelecionado?.value === 3 && (
					<>
						<Label>Revenda</Label>
						<Field>
							<Dropdown
								options={revendas}
								onSelect={(option: Option) => handleSelectOption('revenda', option)}
								selected={revendaSelecionada}
								searchable
							/>
						</Field>
					</>
				)}
				{perfilSelecionado?.value === 2 && (
					<>
						<Label>Concessionárias</Label>
						<Field>
							<RelateDealership>
								<Dropdown
									options={concessionarias}
									onSelect={(option: Option) => setIdConcessionaria(option.value)}
									selected={concessionariaSelecionada}
									searchable
								/>
								<Button
									variant='contained'
									onClick={handleRelateConcessionaria}
									fitContent
									type='button'
								>
									<span>Relacionar</span>
								</Button>
							</RelateDealership>
						</Field>
						<Label></Label>
						<Field>
							{concessionariasRelacionadas.length ? (
								<Table
									columns={tableColumns}
									rows={concessionariasRelacionadas?.map(rowMapper)}
									paginate
								/>
							) : (
								<EmptyRelatedDealerships>
									<Report />{' '}
									<span>Ainda não há concessionárias relacionadas para esse usuário.</span>
								</EmptyRelatedDealerships>
							)}
						</Field>
					</>
				)}
			</Form>
		</RegistrationFormPage>
	);
};

export default NewUser;
